/**
 * pathwaySettings
 * ---------------
 * Per-organisation, per-pathway handover settings (TDI-385, deliverable 1 of
 * the TDI-379 EMIS admin handover spec): how a pathway's referral evidence is
 * exported, and whether leaflets are sent with the triage outcome letter.
 *
 * The Admin Handover screen, the Send letter confirmation and the document
 * exports all read these settings; none of them may hard-code behaviour per
 * pathway. The two BTH services file documents differently — the ADHD service
 * wants each document as its own file and sends leaflets with the letter, the
 * autism service wants one evidence pack and writes its signposting into the
 * letter — and those service choices are data here, not code in the consumers,
 * so a service changing its mind changes this file only.
 *
 * Mirrors python-functions/constants/rms_pathway_settings.py — keep in sync
 * (the functions read the same settings for rmsExportReferralDocument and the
 * letter send). A parity test pins the two files to each other.
 */

import { PATHWAY } from '../utils/triagePathways';

/** How a pathway's referral evidence is exported for EMIS filing. */
export const EVIDENCE_FORMAT = {
  // One PDF per document (each form, each SNAP-IV, each upload): EMIS cannot
  // scroll a document, so the ADHD service files each item separately.
  SEPARATE_DOCUMENTS: 'separate_documents',
  // One evidence pack PDF with a contents page, plus a copyable EMIS comment.
  EVIDENCE_PACK: 'evidence_pack',
};

// The spec's defaults (TDI-379 Pathway settings table). Every organisation
// starts here; only differences go in the overrides below.
const DEFAULT_PATHWAY_SETTINGS = {
  [PATHWAY.ADHD]: {
    evidenceFormat: EVIDENCE_FORMAT.SEPARATE_DOCUMENTS,
    leafletsWithLetter: true,
  },
  [PATHWAY.ASD]: {
    evidenceFormat: EVIDENCE_FORMAT.EVIDENCE_PACK,
    leafletsWithLetter: false,
  },
};

// Per-org overrides, merged over the defaults per pathway. Only list what
// differs (same shape as ORG_FEATURE_OVERRIDES in orgFeatures.js). No
// organisation differs from the defaults today.
const ORG_PATHWAY_SETTINGS_OVERRIDES = {};

/**
 * Who a family contacts about each service's referral (TDI-473): shown when
 * they choose "Contact the service instead" of withdrawing in the portal,
 * and after the outcome letter, when withdrawing here is no longer possible.
 * One entry per pathway, because a combined referral is two services.
 *
 * Placeholders until BTH confirms its public names and details (TDI-315):
 * both services currently carry the Options4CYP line the referral pages
 * already show. Per-org overrides go in SERVICE_CONTACT_OVERRIDES.
 */
const DEFAULT_SERVICE_CONTACTS = {
  [PATHWAY.ADHD]: {
    name: 'ADHD',
    email: 'bfwh.options4cyp@nhs.net',
    phoneLabel: '0800 121 7762 (Option 5)',
    phoneHref: 'tel:08001217762',
  },
  [PATHWAY.ASD]: {
    name: 'Autism',
    email: 'bfwh.options4cyp@nhs.net',
    phoneLabel: '0800 121 7762 (Option 5)',
    phoneHref: 'tel:08001217762',
  },
};
const SERVICE_CONTACT_OVERRIDES = {};

/**
 * The contact details for one service's referral, or null for an unknown
 * pathway. Same resolution as getPathwaySettings: defaults, then any per-org
 * override.
 *
 * @param {string} [orgId]
 * @param {string} pathway - 'adhd' | 'asd'
 * @returns {{ name: string, email: string, phoneLabel: string, phoneHref: string } | null}
 */
export function getServiceContact(orgId, pathway) {
  const key = String(pathway || '').trim().toLowerCase();
  const defaults = DEFAULT_SERVICE_CONTACTS[key];
  if (!defaults) return null;
  const override = (SERVICE_CONTACT_OVERRIDES[orgId] || {})[key] || {};
  return { ...defaults, ...override };
}

/**
 * Resolve the handover settings for one pathway of one organisation.
 * Always returns a complete object for a known pathway: the pathway's defaults
 * spread first, then any per-org override. Unknown or undefined org ids get
 * the defaults untouched; an unknown pathway (including 'both' — combined
 * referrals are handled per pathway, never as one) returns null so the caller
 * cannot half-apply settings that do not exist.
 *
 * @param {string} [orgId]
 * @param {string} pathway - 'adhd' | 'asd' (PATHWAY.*)
 * @returns {{ evidenceFormat: string, leafletsWithLetter: boolean } | null}
 */
export function getPathwaySettings(orgId, pathway) {
  const key = String(pathway || '').trim().toLowerCase();
  const defaults = DEFAULT_PATHWAY_SETTINGS[key];
  if (!defaults) return null;
  const override = (ORG_PATHWAY_SETTINGS_OVERRIDES[orgId] || {})[key] || {};
  return { ...defaults, ...override };
}

/**
 * Whether this pathway's evidence exports as one evidence pack (true) or as
 * separate documents (false). Unknown pathways default to separate documents,
 * the behaviour every export has today.
 *
 * @param {string} [orgId]
 * @param {string} pathway
 * @returns {boolean}
 */
export function usesEvidencePack(orgId, pathway) {
  return getPathwaySettings(orgId, pathway)?.evidenceFormat === EVIDENCE_FORMAT.EVIDENCE_PACK;
}

/**
 * Whether leaflets accompany this pathway's outcome letter send. Unknown
 * pathways send no leaflets.
 *
 * @param {string} [orgId]
 * @param {string} pathway
 * @returns {boolean}
 */
export function leafletsGoWithLetter(orgId, pathway) {
  return getPathwaySettings(orgId, pathway)?.leafletsWithLetter === true;
}
